import { types } from 'mobx-state-tree';

export const SingleItemData = types
  .model('SingleItemData', {
    id: types.string,
    systemCode: types.string,
    accountId: types.string,
    currency: types.string,
    sellerAccountId: types.string,
    sourceMarketplaceId: types.string,
    destinationMarketplaceId: types.string,
    title: types.string,
    status: types.string,
    orderPlacedTime: types.string,
    purchaseTime: types.string,
    listDate: types.string,
    estimatedShippingTime: types.maybe(types.string),
    destinationAccount: types.string,
    destinationOrderId: types.string,
    quantity: types.number,
    monitoringStatus: types.maybe(types.string),
    salePrice: types.maybe(types.union(types.string, types.number)),
    sourcePrice: types.maybe(types.union(types.string, types.number)),
    tax: types.maybe(types.union(types.string, types.number)),
    ebayFee: types.maybe(types.union(types.string, types.number)),
    paypalFee: types.maybe(types.union(types.string, types.number)),
    shippingCollected: types.maybe(types.union(types.string, types.number)),
    profit: types.number,
    imageUrl: types.string,
    globalShippingProgram: types.boolean,
    isBuyItForMe: types.maybe(types.boolean),
    estimatedArrivalDate: types.maybe(types.string),
    buyerUsername: types.maybe(types.string),
    buyerName: types.maybe(types.string),
    buyerEmail: types.maybe(types.string),
    shippingMethod: types.maybe(types.string),
    shippingBuyerName: types.maybe(types.string),
    buyerAddressLine1: types.maybe(types.string),
    buyerAddressLine2: types.maybe(types.string),
    buyerAddressCity: types.maybe(types.string),
    buyerAddressZipCode: types.maybe(types.string),
    buyerAddressState: types.maybe(types.string),
    buyerAddressCountry: types.maybe(types.string),
    buyerPhoneNumber: types.maybe(types.string),
    originalTrackingNumber: types.maybe(types.string),
    originalCarrier: types.maybe(types.string),
    trackingNumber: types.maybe(types.string),
    carrier: types.maybe(types.string),
    sourceAccount: types.maybe(types.string),
    sourceOrderNumber: types.maybe(types.string),
    failedReason: types.maybe(types.string),
    sourceItemUrl: types.maybe(types.string),
    sourceItemCode: types.maybe(types.string),
    destinationItemUrl: types.maybe(types.string),
    destinationItemId: types.maybe(types.string),
    note: types.maybe(types.string),
    openCase: types.boolean,
    returnCase: types.boolean,
    lateShipment: types.boolean,
    listingItemId: types.maybe(types.string),
    sourceTrackingUrl: types.maybe(types.string),
    autoPurchaseFee: types.maybe(types.union(types.string, types.number)),
    taxMargin: types.maybe(types.union(types.string, types.number)),
    manualTotalPrice: types.maybe(types.union(types.string, types.number)),
    isGlobalOrder: types.maybe(types.boolean),
    internationalCarrier: types.maybe(types.string),
    internationalTrackingNumber: types.maybe(types.string),
    deliveryMessage: types.maybe(types.string),
  })
  .views(self => ({
    get invalidSourceTrackingUrl() {
      return (
        self.sourceTrackingUrl &&
        !/^https:\/\/www\.amazon\.com\/progress-tracker/.test(self.sourceTrackingUrl)
      );
    },
    get activeSourcePrice() {
      return self.manualTotalPrice ?? self.sourcePrice;
    },
  }))
  .actions(self => ({
    recalculateProfit() {
      const {
        salePrice,
        shippingCollected,
        activeSourcePrice,
        autoPurchaseFee,
        ebayFee,
        paypalFee,
      } = self;
      if (
        typeof salePrice === 'number' &&
        typeof shippingCollected === 'number' &&
        typeof activeSourcePrice === 'number' &&
        typeof autoPurchaseFee === 'number' &&
        typeof ebayFee === 'number' &&
        typeof paypalFee === 'number'
      ) {
        self.profit =
          salePrice +
          shippingCollected -
          (activeSourcePrice + autoPurchaseFee + ebayFee + paypalFee);
      }
    },
    parseAllPricing() {
      const {
        salePrice,
        shippingCollected,
        sourcePrice,
        ebayFee,
        paypalFee,
        autoPurchaseFee,
        manualTotalPrice,
      } = self;
      self.salePrice = parseFloat(salePrice) || 0;
      self.shippingCollected = parseFloat(shippingCollected) || 0;
      self.sourcePrice = parseFloat(sourcePrice) || 0;
      self.autoPurchaseFee = parseFloat(autoPurchaseFee) || 0;
      self.ebayFee = parseFloat(ebayFee) || 0;
      self.paypalFee = parseFloat(paypalFee) || 0;
      if (manualTotalPrice !== null) {
        self.manualTotalPrice = parseFloat(manualTotalPrice); // avoid giving it default 0 value
      }
    },
    setSalePrice(e) {
      const parsed = e.target.value ? parseFloat(e.target.value) : '';
      if (!isNaN(parsed) && parsed >= 0) {
        self.salePrice = parsed;
        self.recalculateProfit();
      }
      // this test allows strings like "20.0" to be in the input, so that the user may continue to write and get to a number like 20.05
      // considering parseFloat parses "20.0" and "20." to 20, having it as the input's value would block the user from adding "."
      // this condition is useful only when dealing with values changing in real time while the user writes a number
      if (/^\d+\.0?$/.test(e.target.value)) {
        self.salePrice = e.target.value;
      }
    },
    setManualTotalPrice(e) {
      const parsed = e.target.value ? parseFloat(e.target.value) : '';
      if (!isNaN(parsed) && parsed >= 0) {
        self.manualTotalPrice = parsed;
        self.recalculateProfit();
      }
      if (/^\d+\.0?$/.test(e.target.value)) {
        self.manualTotalPrice = e.target.value;
      }
    },
    setEbayFee(e) {
      const parsed = e.target.value ? parseFloat(e.target.value) : '';
      if (!isNaN(parsed) && parsed >= 0) {
        self.ebayFee = parsed;
        self.recalculateProfit();
      }
      if (/^\d+\.0?$/.test(e.target.value)) {
        self.ebayFee = e.target.value;
      }
    },
    setPaypalFee(e) {
      const parsed = e.target.value ? parseFloat(e.target.value) : '';
      if (!isNaN(parsed) && parsed >= 0) {
        self.paypalFee = parsed;
        self.recalculateProfit();
      }
      if (/^\d+\.0?$/.test(e.target.value)) {
        self.paypalFee = e.target.value;
      }
    },
    setShippingCollected(e) {
      const parsed = e.target.value ? parseFloat(e.target.value) : '';
      if (!isNaN(parsed) && parsed >= 0) {
        self.shippingCollected = parsed;
        self.recalculateProfit();
      }
      if (/^\d+\.0?$/.test(e.target.value)) {
        self.shippingCollected = e.target.value;
      }
    },
    setSourceTrackingUrl(e) {
      self.sourceTrackingUrl = e.target.value;
    },
    setAddressLine1(e) {
      self.buyerAddressLine1 = e.target.value;
    },
    setAddressLine2(e) {
      self.buyerAddressLine2 = e.target.value;
    },
    setCity(e) {
      self.buyerAddressCity = e.target.value;
    },
    setBuyerName(e) {
      self.buyerName = e.target.value;
    },
    setBuyerEmail(e) {
      self.buyerEmail = e.target.value;
    },
    setShippingMethod(e) {
      self.shippingMethod = e.target.value;
    },
    setShippingBuyerName(e) {
      self.shippingBuyerName = e.target.value;
    },
    setZipCode(e) {
      self.buyerAddressZipCode = e.target.value;
    },
    setState(e) {
      self.buyerAddressState = e.target.value;
    },
    setCountry(e) {
      self.buyerAddressCountry = e.target.value;
    },
    setPhoneNumber(e) {
      self.buyerPhoneNumber = e.target.value;
    },
    setTrackingNumber(e) {
      self.trackingNumber = e.target.value;
    },
    setOriginalTrackingNumber(e) {
      self.originalTrackingNumber = e.target.value;
    },
    setCarrier(e) {
      self.carrier = e.target.value;
    },
    setOriginalCarrier(e) {
      self.originalCarrier = e.target.value;
    },
    setSourceAccount(e) {
      self.sourceAccount = e.target.value;
    },
    setSourceOrderNum(e) {
      self.sourceOrderNumber = e.target.value;
    },
    setNote(e, fromModal = false) {
      self.note = fromModal ? e : e.target.value;
    },
    setStatus(value) {
      self.status = value;
    },
    setFailedReason(value) {
      self.failedReason = value;
    },
    setMonitoringStatus(value) {
      self.monitoringStatus = value;
    },
  }));
